import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, SITE_URL } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaBlock from "@/components/CtaBlock";
import { Star, Quote } from "lucide-react";

export default function Reviews() {
  const { company } = useCompany();
  const { data: reviews = [] } = useQuery({ queryKey: ["reviews-page"], queryFn: async () => (await api.get("/reviews")).data });
  const avg = reviews.length ? (reviews.reduce((a, r) => a + (r.rating || 5), 0) / reviews.length).toFixed(1) : "5.0";

  return (
    <>
      <Seo title="Customer Reviews & Testimonials | Aquila Air & Heating" description="Read what Space Coast homeowners say about Aquila Air & Heating. Real reviews for AC repair, installation, and HVAC service across Brevard County." path="/reviews/" />
      <section className="bg-navy text-white py-12 md:py-16 dot-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Reviews" }]} /></div>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight">Customer Reviews</h1>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex gap-0.5 text-launch">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-5 w-5 fill-current" />)}</div>
            <span className="text-lg font-semibold">{avg} average from {reviews.length}+ happy customers</span>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <div key={r.id} data-testid={`review-card-${r.id}`} className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col shadow-sm">
                <Quote className="h-8 w-8 text-cool/40" />
                <div className="mt-2 flex gap-0.5 text-launch">{Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
                <p className="mt-3 text-slate-700 leading-relaxed flex-1">"{r.text}"</p>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <div><span className="block font-semibold text-navy">{r.name}</span><span className="block text-sm text-slate-500">{r.location}</span></div>
                  <span className="text-xs text-slate-400">{r.source}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-14 md:pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 prose-launch">
          <h2 className="text-2xl font-bold text-navy">Why Space Coast Homeowners Trust Aquila Air &amp; Heating</h2>
          <p className="mt-3 text-slate-700 leading-relaxed">
            Space Coast homeowners trust Aquila Air &amp; Heating for fast, honest, and expertly performed HVAC
            service, and their reviews tell the story. Across Cocoa, Rockledge, Merritt Island, Titusville,
            Melbourne, and the wider Brevard County area, our customers consistently praise our quick response
            times, transparent and upfront pricing, and the care our licensed technicians bring to every home.
          </p>
          <p className="mt-4 text-slate-700 leading-relaxed">
            When your air conditioner quits in the middle of a Florida heat wave, you need a team that answers the
            call, shows up on time, and fixes the problem right the first time. Our reviews reflect that commitment:
            same-day and 24/7 emergency repairs, clear explanations of what is wrong and what your options are, and
            no-pressure recommendations that respect your home and budget. Homeowners frequently mention how our
            comfort advisors take the time to explain repair-versus-replace decisions, walk through efficient new
            system options, and follow through after the work is done.
          </p>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Reading real reviews is one of the best ways to choose an HVAC company, and we encourage you to see what
            Space Coast families are saying about their experience with our team. If you have worked with Aquila Air
            &amp; Heating, we would be grateful if you shared your own review to help other homeowners find dependable
            comfort. And if you are looking for a trustworthy air conditioning and heating partner for repairs,
            maintenance, or a new high-efficiency system, we would love the opportunity to earn your five-star review too.
          </p>
        </div>
      </section>
      <CtaBlock heading="Join Our Happy Customers" />
    </>
  );
}
